import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, Chip, Dialog, DialogContent, Divider, Grid, Typography } from '@mui/material';

import { useAppContext } from '../../Context';
import { useGroups, usePermissions } from '../../services';
import { Button } from '../../components/shared/FormElements';
import { useAppDispatch } from '../../hooks';
import { setPopAlert, setPopConfirmation } from '../../slices/miscSlice';
import { ProcessType } from '../../types';
import { constants } from '../../constants';

type RepositionType = {
  id: number;
  order_process?: any;
  order_id?: number;
  order_process_id?: number;
  order_process_status?: string;
  current_process_id?: number;
  current_process_name?: string;
  process_origin?: any;
  process_origin_name?: string;
  reason?: string;
  status?: string;
  created_at?: string;
  created_by?: any;
  created_by_name?: string;
  created_by_username?: string;
  evidences?: any[];
};

type RepositionLogType = {
  id: number;
  reposition_id?: number;
  action?: string;
  status?: string;
  comment?: string;
  created_by?: any;
  created_at?: string;
};

const ViewRepositionDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const {
    canView_repositions,
    canView_repositions_all,
    canView_repositions_assigned,
    canChange_reposition,
    canResolve_repositions,
    canCancel_repositions,
    canDelete_reposition,
  } = usePermissions([
    'view_repositions',
    'view_repositions_all',
    'view_repositions_assigned',
    'change_reposition',
    'resolve_repositions',
    'cancel_repositions',
    'delete_reposition',
  ]);
  const { isAdministrador } = useGroups([constants.groups.GROUP_ADMINISTRADOR]);
  const canViewRepositions = canView_repositions || canView_repositions_all || canView_repositions_assigned;

  const [reposition, setReposition] = useState<RepositionType | null>(null);
  const [processes, setProcesses] = useState<ProcessType[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [formProcessOrigin, setFormProcessOrigin] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formEvidences, setFormEvidences] = useState<File[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string>('');
  const [repositionLogs, setRepositionLogs] = useState<RepositionLogType[]>([]);

  useEffect(() => {
    if (!canViewRepositions) {
      navigate('/');
      return;
    }
    if (!id) {
      navigate('/reposiciones');
      return;
    }
    getReposition(id);
    getRepositionLogs(id);
    getProcesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getReposition = async (repositionId: string) => {
    const response = await apiCall(
      'GET',
      `repositions/${repositionId}/`,
      null,
      '',
      'No se pudo obtener la reposición',
      true
    );
    if (response) {
      setReposition(response as RepositionType);
    }
  };

  const getProcesses = async () => {
    const response = await apiCall('GET', 'processes/', null, '', 'No se pudo obtener los procesos', true);
    if (response) {
      setProcesses(response as ProcessType[]);
    }
  };

  const getRepositionLogs = async (repositionId: string) => {
    const response = await apiCall(
      'GET',
      `repositions/${repositionId}/logs/`,
      null,
      '',
      'No se pudo obtener los logs de la reposicion',
      true
    );
    const logs = response ? (response as RepositionLogType[]) : [];
    setRepositionLogs(logs);
    return logs;
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const refreshRepositionLogs = async (repositionId: string, previousCount = 0) => {
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const logs = await getRepositionLogs(repositionId);
      if (logs.length > previousCount) {
        return;
      }
      if (attempt < maxAttempts - 1) {
        await wait(500);
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const evidenceUrls = useMemo(() => {
    if (!reposition?.evidences || !Array.isArray(reposition.evidences)) return [];
    return reposition.evidences
      .map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (item.file) return item.file;
        if (item.image) return item.image;
        if (item.url) return item.url;
        return null;
      })
      .filter(Boolean) as string[];
  }, [reposition]);

  const normalizeFileUrl = (value: string) => {
    if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('/')) {
      return value;
    }
    return `${process.env.REACT_APP_FILES_URL}${value}`;
  };

  const handleOpenLightbox = (url: string) => {
    setLightboxImage(url);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  const canEditRepositions = canChange_reposition;
  const canValidateReposition = canResolve_repositions || canEditRepositions || isAdministrador;
  const canCancelReposition = canCancel_repositions || canEditRepositions || isAdministrador;
  const canDeleteReposition = canDelete_reposition || isAdministrador;
  const actionButtonStyle: CSSProperties = {
    border: '1px solid #fff',
    boxShadow: '0 0 0 1px #fff inset',
    maxWidth: 250,
    flex: '0 0 auto',
  };

  const ensureCanEdit = () => {
    if (canEditRepositions) return true;
    dispatch(setPopAlert({ message: 'No tienes permisos para editar reposiciones', show: true, alertType: 'danger' }));
    return false;
  };

  const processOriginId = useMemo(() => {
    if (reposition?.process_origin && typeof reposition.process_origin === 'number') {
      return reposition.process_origin.toString();
    }
    if (reposition?.process_origin && typeof reposition.process_origin === 'string') {
      return reposition.process_origin;
    }
    if (reposition?.process_origin && typeof reposition.process_origin === 'object' && reposition.process_origin.id) {
      return reposition.process_origin.id.toString();
    }
    return '';
  }, [reposition]);

  const orderProcessLabel = useMemo(() => {
    if (reposition?.order_id) return `#${reposition.order_id}`;
    const orderProcess = reposition?.order_process;
    if (!orderProcess) return '-';
    if (typeof orderProcess === 'number' || typeof orderProcess === 'string') {
      return orderProcess.toString();
    }
    if (orderProcess.order_id) return `#${orderProcess.order_id}`;
    if (orderProcess.id) return `#${orderProcess.id}`;
    return '-';
  }, [reposition]);

  const processOriginLabel = useMemo(() => {
    if (reposition?.process_origin_name) return reposition.process_origin_name;
    const origin = reposition?.process_origin;
    if (!origin) return '-';
    if (typeof origin === 'string') return origin;
    if (origin.process_name) return origin.process_name;
    if (origin.name) return origin.name;
    return '-';
  }, [reposition]);

  const createdByLabel = useMemo(() => {
    if (reposition?.created_by_name) return reposition.created_by_name;
    if (reposition?.created_by_username) return reposition.created_by_username;
    const createdBy = reposition?.created_by;
    if (!createdBy) return '-';
    if (typeof createdBy === 'string') return createdBy;
    if (createdBy.name) return createdBy.name;
    if (createdBy.username) return createdBy.username;
    return '-';
  }, [reposition]);

  const statusLabel = useMemo(() => {
    if (!reposition?.status) return '-';
    return reposition.status.toString();
  }, [reposition]);

  const isRepositionCanceled = useMemo(() => {
    const status = reposition?.status?.toString().toLowerCase();
    return status === 'cancelada' || status === 'cancelado' || status === 'cancelada';
  }, [reposition]);

  const isRepositionResolved = useMemo(() => {
    const status = reposition?.status?.toString().toLowerCase();
    return status === 'resuelta' || status === 'resuelto';
  }, [reposition]);

  const isRepositionCreated = useMemo(() => {
    const status = reposition?.status?.toString().toLowerCase();
    return status === 'creada' || status === 'creado';
  }, [reposition]);

  const statusColor = useMemo(() => {
    switch (reposition?.status) {
      case 'CREADA':
      case 'Creada':
        return 'info';
      case 'RESUELTA':
      case 'Resuelta':
        return 'success';
      case 'EN_PROCESO':
      case 'En Proceso':
        return 'warning';
      case 'CANCELADA':
      case 'Cancelada':
        return 'error';
      default:
        return 'default';
    }
  }, [reposition]);

  const statusColors: Record<string, string> = {
    creada: 'var(--blueLight)',
    'en proceso': 'var(--orange-color)',
    finalizado: 'var(--green-color)',
    resuelta: 'var(--green-color)',
    cancelada: 'var(--red-color)',
    bloqueado: 'var(--red-color)',
  };

  const getProcessStatusColor = (status: string) => {
    const normalized = status.trim().toLowerCase();
    return statusColors[normalized] || 'var(--blueLight)';
  };

  const renderProcessStatusPill = (status: string) => {
    if (!status) return '-';
    return (
      <span className='pill' style={{ backgroundColor: getProcessStatusColor(status) }}>
        {status}
      </span>
    );
  };

  const renderStatusPill = (status: string) => {
    if (!status) return '-';
    return (
      <span className='pill' style={{ backgroundColor: getProcessStatusColor(status) }}>
        {status}
      </span>
    );
  };

  const getLogCreatedBy = (createdBy?: any) => {
    if (!createdBy) return '-';
    if (typeof createdBy === 'string') return createdBy;
    const name = `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim();
    if (name) return name;
    if (createdBy.username) return createdBy.username;
    return '-';
  };

  const parseLogAction = (action?: string) => {
    if (!action) return { actionLabel: '-', statusLabel: '' };
    if (action.startsWith('STATUS_')) {
      const rawStatus = action.replace('STATUS_', '').replace(/_/g, ' ');
      return { actionLabel: 'Cambio de estado', statusLabel: rawStatus };
    }
    return { actionLabel: action.replace(/_/g, ' '), statusLabel: '' };
  };

  const orderProcessId = useMemo(() => {
    if (typeof reposition?.order_process_id === 'number') return reposition.order_process_id.toString();
    return '';
  }, [reposition]);

  const currentProcessId = useMemo(() => {
    if (typeof reposition?.current_process_id === 'number') return reposition.current_process_id.toString();
    return '';
  }, [reposition]);

  const currentProcessName = useMemo(() => {
    if (reposition?.current_process_name) return reposition.current_process_name;
    if (currentProcessId && processes.length > 0) {
      const matched = processes.find((process) => process.id.toString() === currentProcessId);
      if (matched?.process_name) return matched.process_name;
    }
    return '';
  }, [currentProcessId, processes, reposition]);

  const orderProcessStatus = useMemo(() => {
    if (!reposition?.order_process_status) return '';
    return reposition.order_process_status.toString();
  }, [reposition]);

  const hasProcessInfo = Boolean(orderProcessId || currentProcessName || orderProcessStatus);

  useEffect(() => {
    if (!reposition) return;
    if (!canEditRepositions && editMode) {
      setEditMode(false);
    }
    setFormProcessOrigin(processOriginId);
    setFormReason(reposition.reason || '');
    setFormEvidences([]);
  }, [reposition, processOriginId, canEditRepositions, editMode]);

  const handleEditAction = async () => {
    if (!ensureCanEdit()) return;
    if (!editMode) {
      setEditMode(true);
      return;
    }
    if (!formProcessOrigin) {
      dispatch(setPopAlert({ message: 'Debe seleccionar el proceso de origen', show: true, alertType: 'danger' }));
      return;
    }
    if (!formReason.trim()) {
      dispatch(setPopAlert({ message: 'Debe escribir la razón del reporte', show: true, alertType: 'danger' }));
      return;
    }

    const hasFiles = formEvidences.length > 0;
    const orderProcessValue = reposition?.order_process ?? '';
    let body: any = {
      order_process: orderProcessValue,
      process_origin: parseInt(formProcessOrigin, 10),
      reason: formReason,
    };

    if (hasFiles) {
      const formData = new FormData();
      formData.append('order_process', orderProcessValue.toString());
      formData.append('process_origin', formProcessOrigin);
      formData.append('reason', formReason);
      formEvidences.forEach((file) => formData.append('evidences', file));
      body = formData;
    }

    const response = await apiCall(
      'PUT',
      `repositions/${reposition?.id}/`,
      body,
      'Reposición actualizada',
      'Error al actualizar la reposición',
      hasFiles ? { isFormData: true } : undefined
    );
    if (response) {
      setReposition(response as RepositionType);
      setEditMode(false);
      setFormEvidences([]);
      const logId = (response as RepositionType)?.id?.toString() || reposition?.id?.toString() || '';
      if (logId) {
        refreshRepositionLogs(logId, repositionLogs.length);
      }
    }
  };

  const handleSendToProduction = async () => {
    if (!ensureCanEdit()) return;
    if (!reposition?.order_id || !processOriginId) {
      dispatch(setPopAlert({ message: 'Faltan datos para enviar a producción', show: true, alertType: 'danger' }));
      return;
    }
    const payload = {
      order_id: reposition.order_id,
      process_id: parseInt(processOriginId, 10),
      process_status: 'Por Iniciar',
      comment: 'Creado a partir de una reposición',
      repositionId: reposition.id,
    };
    const created = await apiCall(
      'POST',
      'order_process/',
      payload,
      'Reposición enviada a producción',
      'Error al enviar a producción',
      true
    );
    if (created) {
      const updated = await apiCall(
        'PUT',
        `repositions/${reposition.id}/`,
        { status: 'En Proceso' },
        'Reposición actualizada',
        'Error al actualizar el estado de la reposición',
        true
      );
      if (updated) {
        getReposition(reposition.id.toString());
        refreshRepositionLogs(reposition.id.toString(), repositionLogs.length);
      }
    }
  };

  const handleCancelReposition = async () => {
    if (!ensureCanEdit()) return;
    if (!reposition?.id) return;
    const updated = await apiCall(
      'PUT',
      `repositions/${reposition.id}/`,
      { status: 'CANCELADA' },
      'Reposición cancelada',
      'Error al cancelar la reposición',
      true
    );
    if (updated) {
      getReposition(reposition.id.toString());
      refreshRepositionLogs(reposition.id.toString(), repositionLogs.length);
    }
  };

  const handleConfirmSendToProduction = () => {
    if (!ensureCanEdit()) return;
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea validar y enviar a producción esta reposición?',
        alertType: 'danger',
        okFunc: () => handleSendToProduction(),
        okText: 'Confirmar',
        cancelText: 'No',
      })
    );
  };

  const handleConfirmCancelReposition = () => {
    if (!ensureCanEdit()) return;
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea cancelar esta reposición?',
        alertType: 'danger',
        okFunc: () => handleCancelReposition(),
        okText: 'Confirmar',
        cancelText: 'No',
      })
    );
  };

  const handleDeleteReposition = async () => {
    if (!reposition?.id) return;
    const deleted = await apiCall(
      'DELETE',
      `repositions/${reposition.id}/delete/`,
      null,
      'Reposición eliminada',
      'Error al eliminar la reposición',
      true
    );
    if (deleted) {
      navigate('/reposiciones');
    }
  };

  const handleConfirmDeleteReposition = () => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea eliminar esta reposición? Esta acción no se puede deshacer.',
        alertType: 'danger',
        okFunc: () => handleDeleteReposition(),
        okText: 'Confirmar',
        cancelText: 'No',
      })
    );
  };

  const handleDiscardChanges = () => {
    if (!reposition) {
      setEditMode(false);
      return;
    }
    setFormProcessOrigin(processOriginId);
    setFormReason(reposition.reason || '');
    setFormEvidences([]);
    setEditMode(false);
  };

  if (!reposition) {
    return (
      <div className='container-wrap'>
        <Typography variant='h6'>Cargando reposición...</Typography>
      </div>
    );
  }

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8} className='text-left'>
          <h2 className='text-left'>Reposición #{reposition.id}</h2>
        </Grid>
        <Grid item xs={12} sm={4} className='text-right'>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button className='btn btn-primary' onClick={() => navigate('/reposiciones')}>
              Volver
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Detalles
              </Typography>
              <Box sx={{ mb: 1 }}>
                <strong>ID:</strong> {reposition.id}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Orden:</strong> {orderProcessLabel}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Zona del daño:</strong>{' '}
                {editMode ? (
                  <select
                    value={formProcessOrigin}
                    onChange={(e) => setFormProcessOrigin(e.target.value)}
                    className='form-control'
                  >
                    <option value=''>Seleccione un proceso</option>
                    {processes.map((process) => (
                      <option key={process.id} value={process.id}>
                        {process.process_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  processOriginLabel
                )}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Reportado por:</strong> {createdByLabel}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Estado:</strong>{' '}
                <Chip label={statusLabel} color={statusColor as any} size='small' />
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Fecha:</strong> {formatDate(reposition.created_at)}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Proceso relacionado
              </Typography>
              {hasProcessInfo ? (
                <>
                  <Box sx={{ mb: 1 }}>
                    <strong>#:</strong> {orderProcessId || '-'}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Proceso Actual:</strong> {currentProcessName || '-'}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Estado actual del proceso:</strong> {renderProcessStatusPill(orderProcessStatus)}
                  </Box>
                </>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No hay ningun proceso relacionado, falta validar y enviar a producción la reposición.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Razón del reporte
              </Typography>
              {editMode ? (
                <textarea
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  rows={5}
                  className='form-control'
                />
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  {reposition.reason || '-'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Evidencias
              </Typography>
              {editMode ? (
                <Box sx={{ mb: 2 }}>
                  <input
                    type='file'
                    multiple
                    accept='image/*'
                    onChange={(e) => setFormEvidences(Array.from(e.target.files || []))}
                  />
                </Box>
              ) : null}
              {evidenceUrls.length === 0 ? (
                <Typography variant='body2' color='textSecondary'>
                  No hay evidencias adjuntas.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {evidenceUrls.map((url, index) => (
                    <Box key={index} sx={{ width: 180 }}>
                      <Box
                        component='button'
                        type='button'
                        onClick={() => handleOpenLightbox(url)}
                        sx={{
                          border: 'none',
                          padding: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        <img
                          src={normalizeFileUrl(url)}
                          alt={`evidencia_${index}`}
                          style={{ width: '100%', borderRadius: 6 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        {canEditRepositions ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
              {!hasProcessInfo && isRepositionCreated ? (
                <Button
                  className='btn btn-secondary'
                  onClick={handleEditAction}
                  style={actionButtonStyle}
                >
                  {editMode ? 'Guardar cambios' : 'Editar'}
                </Button>
              ) : null}
              {!hasProcessInfo && isRepositionCreated ? (
                editMode ? (
                  <Button
                    className='btn btn-secondary'
                    onClick={handleDiscardChanges}
                    style={actionButtonStyle}
                  >
                    No Guardar Cambio
                  </Button>
                ) : (
                  canValidateReposition && (
                    <Button
                      className='btn btn-secondary'
                      onClick={handleConfirmSendToProduction}
                      style={actionButtonStyle}
                    >
                      Validar y enviar a produccion
                    </Button>
                  )
                )
              ) : null}
              {editMode || isRepositionCanceled || isRepositionResolved ? null : (
                canCancelReposition && (
                  <Button
                    className='btn btn-secondary'
                    onClick={handleConfirmCancelReposition}
                    style={actionButtonStyle}
                  >
                    CANCELAR REPOSICIÓN
                  </Button>
                )
              )}
              {canDeleteReposition && !editMode && (!hasProcessInfo || isRepositionCanceled) ? (
                <Button
                  className='btn btn-secondary'
                  onClick={handleConfirmDeleteReposition}
                  style={actionButtonStyle}
                >
                  ELIMINAR REPOSICIÓN
                </Button>
              ) : null}
            </Box>
          </Grid>
        ) : null}
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Logs de reposicion
              </Typography>
              {repositionLogs.length === 0 ? (
                <Typography variant='body2' color='textSecondary'>
                  No hay logs registrados.
                </Typography>
              ) : (
                <div className='table-responsive'>
                  <table
                    className='table'
                    style={{
                      width: '100%',
                      borderCollapse: 'separate',
                      borderSpacing: '0 15px',
                      textAlign: 'center',
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Accion</th>
                        <th>Estado</th>
                        <th>Comentario</th>
                        <th>Usuario</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repositionLogs.map((log) => {
                        const { actionLabel, statusLabel } = parseLogAction(log.action);
                        const statusValue = log.status || statusLabel;
                        return (
                          <tr key={log.id}>
                            <td>{renderStatusPill(actionLabel)}</td>
                            <td>{statusValue ? renderStatusPill(statusValue) : '-'}</td>
                            <td>{log.comment || '-'}</td>
                            <td>{getLogCreatedBy(log.created_by)}</td>
                            <td>{formatDate(log.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog open={lightboxOpen} onClose={handleCloseLightbox} maxWidth='md' fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button className='btn btn-secondary' onClick={handleCloseLightbox}>
              Cerrar
            </Button>
          </Box>
          {lightboxImage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={normalizeFileUrl(lightboxImage)}
                alt='evidencia_ampliada'
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 6 }}
              />
            </Box>
          ) : (
            <Typography variant='body2' color='textSecondary'>
              No hay evidencias adjuntas.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewRepositionDetail;
